import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { 
  BookOpenIcon,
  PlayCircleIcon,
  UserGroupIcon,
  PresentationChartLineIcon,
  BuildingOffice2Icon,
  ClipboardDocumentCheckIcon,
  CubeIcon,
  ArrowRightIcon,
  SparklesIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  LightBulbIcon,
  CalendarIcon,
  ShieldCheckIcon,
  IdentificationIcon,
  TruckIcon
} from '@heroicons/react/24/outline'
import api from '../../../services/authService'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'
import SeccionNormatividad from '../../../componentes/panel-institucion/SeccionNormatividad'

interface Grupo {
  id_grupo: number
  nombre: string
  descripcion?: string
}

interface AsignacionAlumno {
  id_asignacion?: number
  id_simulacion: number
  id_grupo?: number
  nombre_grupo?: string
  titulo: string
  descripcion?: string
  tipo: string
  fecha_limite?: string | null
  estado: 'en_curso' | 'pendiente' | 'completado' | 'calificado'
  progreso: number
  puntaje?: number | null
}

interface ResumenAlumno {
  total_grupos: number
  total_asignaciones: number
  activas: number
  completadas: number
  promedio_calificacion?: number | null
}

interface DashboardAlumno {
  vista: 'alumno'
  constitucion: {
    id_constitucion: number
    nombre: string
    descripcion?: string
    tipo?: string
    contacto_email?: string
    contacto_telefono?: string
    rol_miembro?: string
  }
  grupos: Grupo[]
  mis_asignaciones: AsignacionAlumno[]
  resumen_alumno?: ResumenAlumno
}

const ESTILOS_ESTADO: Record<AsignacionAlumno['estado'], { badge: string; dot: string; label: string }> = {
  en_curso: {
    badge: 'border border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-400',
    dot: 'bg-amber-500 animate-pulse',
    label: 'En Curso',
  },
  pendiente: {
    badge: 'border border-slate-300 dark:border-white/10 bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-zinc-400',
    dot: 'bg-slate-400',
    label: 'Pendiente',
  },
  completado: {
    badge: 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    dot: 'bg-emerald-500',
    label: 'Completado',
  },
  calificado: {
    badge: 'border border-indigo-500/20 bg-indigo-500/10 text-indigo-700 dark:text-indigo-400',
    dot: 'bg-indigo-500',
    label: 'Auditado / Calificado',
  },
}

const PaginaPanelUsuarios: React.FC = () => {
  const { usuario } = useAuth()
  const [constitucion, setConstitucion] = useState<{ id_constitucion: number; nombre: string; rol_miembro?: string } | null>(null)
  const [grupos, setGrupos] = useState<Grupo[]>([])
  const [asignaciones, setAsignaciones] = useState<AsignacionAlumno[]>([])
  const [resumenAlumno, setResumenAlumno] = useState<ResumenAlumno | null>(null)
  const [cargando, setCargando] = useState(false)

  // Filtros interactivos de asignaciones
  const [filtroEstado, setFiltroEstado] = useState<'todos' | 'en_curso' | 'pendiente' | 'completado'>('todos')
  const [busqueda, setBusqueda] = useState('')

  const cargar = async () => {
    setCargando(true)
    try {
      const dashboardRes = await api.get<DashboardAlumno>('/api/institucion/dashboard/resumen')
      setConstitucion(dashboardRes.data?.constitucion || null)
      setGrupos(dashboardRes.data?.grupos || [])
      setAsignaciones(dashboardRes.data?.mis_asignaciones || [])
      setResumenAlumno(dashboardRes.data?.resumen_alumno || null)
    } catch (error) {
      console.error('Error cargando datos del dashboard alumno', error)
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => {
    cargar()
  }, [])

  // Métricas calculadas con fallback
  const totalAsignaciones = resumenAlumno?.total_asignaciones ?? asignaciones.length
  const totalActivas = resumenAlumno?.activas ?? asignaciones.filter(a => a.estado === 'en_curso').length
  const totalCompletadas = resumenAlumno?.completadas ?? asignaciones.filter(a => ['completado', 'calificado'].includes(a.estado)).length
  const progresoPromedio = asignaciones.length
    ? Math.round(asignaciones.reduce((acc, sim) => acc + (sim.progreso || 0), 0) / asignaciones.length)
    : 0
  const promedioCalificacion = resumenAlumno?.promedio_calificacion 
    ? Number(resumenAlumno.promedio_calificacion).toFixed(1) 
    : null

  // Simulación prioritaria para el botón del Hero
  const practicaPrincipal = asignaciones.find((sim) => sim.estado === 'en_curso') || asignaciones[0] || null

  // Filtrado reactivo de asignaciones
  const asignacionesFiltradas = useMemo(() => {
    return asignaciones.filter((asig) => {
      if (filtroEstado === 'en_curso' && asig.estado !== 'en_curso') return false
      if (filtroEstado === 'pendiente' && asig.estado !== 'pendiente') return false
      if (filtroEstado === 'completado' && !['completado', 'calificado'].includes(asig.estado)) return false

      if (busqueda.trim()) {
        const q = busqueda.toLowerCase().trim()
        const matchTitulo = (asig.titulo || '').toLowerCase().includes(q)
        const matchTipo = (asig.tipo || '').toLowerCase().includes(q)
        const matchGrupo = (asig.nombre_grupo || '').toLowerCase().includes(q)
        const matchDesc = (asig.descripcion || '').toLowerCase().includes(q)
        return matchTitulo || matchTipo || matchGrupo || matchDesc
      }

      return true
    })
  }, [asignaciones, filtroEstado, busqueda])

  return (
    <div className="min-h-screen space-y-8 pb-14 animate-fadeIn max-w-[1600px] mx-auto">
      {/* ======================================================== */}
      {/* 1. HERO BENTO: TERMINAL DE OPERACIONES ACADÉMICAS WMS   */}
      {/* ======================================================== */}
      <section className="relative overflow-hidden rounded-[32px] border border-slate-200/80 dark:border-white/[0.08] bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl shadow-xl p-6 sm:p-8 md:p-10">
        {/* Luces volumétricas de fondo */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-blue-500/15 blur-3xl" />
        <div className="pointer-events-none absolute left-1/3 -bottom-24 h-80 w-80 rounded-full bg-indigo-500/10 blur-3xl" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div className="space-y-4 max-w-3xl">
            {/* Badges de Estado Operativo */}
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-600 dark:text-emerald-400 text-xs font-black uppercase tracking-wider shadow-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Operador en Línea · Terminal WMS v1.0
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-zinc-300 text-xs font-bold">
                <BuildingOffice2Icon className="h-3.5 w-3.5 text-blue-500" />
                {constitucion?.nombre || 'LogiXport Campus'}
              </span>
              {grupos.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-xs font-bold">
                  <UserGroupIcon className="h-3.5 w-3.5" />
                  {grupos[0].nombre}
                </span>
              )}
            </div>

            {/* Saludo y Visión Operativa */}
            <div className="flex items-center gap-5">
              {usuario?.avatar && (
                <div className="flex-shrink-0">
                  <img 
                    src={usuario.avatar} 
                    alt="Perfil" 
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-[3px] border-white/50 dark:border-white/10 shadow-xl object-cover" 
                    referrerPolicy="no-referrer"
                  />
                </div>
              )}
              <div className="space-y-2">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
                  Hola, <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-500 bg-clip-text text-transparent dark:from-sky-400 dark:via-blue-400 dark:to-indigo-300">
                    {[usuario?.nombre, usuario?.apellido].filter(Boolean).join(' ') || 'Operador'}
                  </span>
                </h1>
                <p className="text-sm md:text-base text-slate-600 dark:text-zinc-400 leading-relaxed font-normal">
                  Consola interactiva de entrenamiento logístico. Controla el inventario en racks 3D, gestiona pedimentos y despacho aduanero, y audita la trazabilidad normativa en tiempo real.
                </p>
              </div>
            </div>

            {/* Botones de Acción Inmediata */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Link
                to="/institucion/almacen-practica"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-blue-500/25 transition-all hover:scale-[1.02] active:scale-[0.99] group"
              >
                <CubeIcon className="h-4 w-4 group-hover:rotate-12 transition-transform duration-300" />
                <span>Entrar a Almacén WMS 3D</span>
                <ArrowRightIcon className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
              </Link>

              <Link
                to={practicaPrincipal ? `/institucion/simulacion/caso-a/${practicaPrincipal.id_simulacion}` : '/institucion/simulaciones'}
                className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-white dark:bg-white/10 hover:bg-slate-50 dark:hover:bg-white/15 text-slate-900 dark:text-white border border-slate-200 dark:border-white/10 text-xs font-bold uppercase tracking-wider shadow-sm transition-all hover:scale-[1.02] active:scale-[0.99]"
              >
                <PlayCircleIcon className="h-4 w-4 text-emerald-500" />
                <span>Continuar Caso Activo</span>
              </Link>

              <Link
                to="/institucion/despacho"
                className="inline-flex items-center gap-2 px-4 py-3.5 rounded-2xl bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/25 text-xs font-bold uppercase tracking-wider transition-all"
              >
                <TruckIcon className="h-4 w-4" />
                <span>Mesa de Despacho</span>
              </Link>
            </div>
          </div>

          {/* Botón de recarga de telemetría */}
          <div className="flex lg:flex-col items-center lg:items-end justify-between gap-3 shrink-0">
            <button
              onClick={cargar}
              disabled={cargando}
              className="p-3.5 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 border border-slate-200/80 dark:border-white/10 transition-all shadow-sm group"
              title="Actualizar datos operativos"
            >
              <ArrowPathIcon className={`h-5 w-5 ${cargando ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
            </button>
            <div className="text-right hidden sm:block">
              <p className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">
                ID INSTITUCIÓN
              </p>
              <p className="text-xs font-mono font-bold text-slate-700 dark:text-zinc-300">
                #{constitucion?.id_constitucion ?? '001'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================================== */}
      {/* 2. LIVE KPI BENTO MATRIX (4 Tarjetas)                   */}
      {/* ======================================================== */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {/* KPI 1: Casos Asignados */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm hover:shadow-xl hover:border-blue-500/40 transition-all group">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Prácticas Asignadas</span>
            <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
              <ClipboardDocumentCheckIcon className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <p className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {totalAsignaciones}
            </p>
            <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
              {totalActivas} en curso
            </span>
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
            Simulaciones dentro de tu plan formativo
          </p>
        </div>

        {/* KPI 2: Progreso Global */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm hover:shadow-xl hover:border-cyan-500/40 transition-all group">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Índice de Progreso</span>
            <div className="p-2.5 rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 group-hover:scale-110 transition-transform">
              <PresentationChartLineIcon className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <p className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {progresoPromedio}%
            </p>
            <span className="text-[11px] font-bold text-slate-400">Meta: 100%</span>
          </div>
          {/* Barra de progreso luminosa */}
          <div className="mt-3 h-2 rounded-full bg-slate-100 dark:bg-white/5 overflow-hidden">
            <div 
              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-700"
              style={{ width: `${progresoPromedio}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
            Avance curricular validado
          </p>
        </div>

        {/* KPI 3: Completadas y Score */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm hover:shadow-xl hover:border-emerald-500/40 transition-all group">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Casos Concluidos</span>
            <div className="p-2.5 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform">
              <CheckCircleIcon className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <p className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {totalCompletadas}
            </p>
            {promedioCalificacion ? (
              <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
                Score: {promedioCalificacion}/100
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-zinc-400 border border-slate-200 dark:border-white/10">
                En evaluación
              </span>
            )}
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
            Operaciones auditadas por docentes
          </p>
        </div>

        {/* KPI 4: Cuadrilla / Grupos */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm hover:shadow-xl hover:border-indigo-500/40 transition-all group">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Cuadrilla / Turno</span>
            <div className="p-2.5 rounded-2xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
              <UserGroupIcon className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <p className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight truncate max-w-[180px]">
              {grupos.length > 0 ? grupos[0].nombre : 'Sin Cuadrilla'}
            </p>
            <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-500/20">
              {grupos.length} grupo(s)
            </span>
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
            Asignación académica institucional
          </p>
        </div>
      </section>

      {/* ======================================================== */}
      {/* 3. CENTRO DE OPERACIONES FORMATIVAS (3 Bento Hubs)      */}
      {/* ======================================================== */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg md:text-xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-blue-500" />
              Estaciones Operativas & Simuladores
            </h2>
            <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium">
              Accede a las herramientas profesionales de entrenamiento logístico y aduanero
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Hub 1: Despacho y Terminal Portuaria */}
          <Link
            to="/institucion/despacho"
            className="group relative overflow-hidden rounded-[26px] p-6 border border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.06] via-white/80 to-transparent dark:from-cyan-500/10 dark:via-[#0c0c0c]/80 dark:to-transparent backdrop-blur-xl shadow-md hover:shadow-2xl hover:border-cyan-500/50 transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border border-cyan-500/25">
                  SSA MANZANILLO & TOS
                </span>
                <div className="p-2.5 rounded-2xl bg-cyan-600 text-white shadow-md shadow-cyan-500/30 group-hover:scale-110 transition-transform">
                  <TruckIcon className="h-5 w-5" />
                </div>
              </div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                Despacho y Puerto
              </h3>
              <p className="mt-2 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
                Mesa interactiva de bookings, Anexo 29, pesaje VGM en báscula BP1, modulación aduanera y bandeja del Alumno Receptor.
              </p>
            </div>
            <div className="mt-6 flex items-center justify-between pt-4 border-t border-slate-100 dark:border-white/[0.06]">
              <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                Abrir Mesa de Despacho <ArrowRightIcon className="h-3.5 w-3.5" />
              </span>
              <span className="text-[10px] font-semibold text-slate-400">Terminal Activa</span>
            </div>
          </Link>

          {/* Hub 2: Almacén de Prácticas WMS */}
          <Link
            to="/institucion/almacen-practica"
            className="group relative overflow-hidden rounded-[26px] p-6 border border-blue-500/20 bg-gradient-to-br from-blue-500/[0.06] via-white/80 to-transparent dark:from-blue-500/10 dark:via-[#0c0c0c]/80 dark:to-transparent backdrop-blur-xl shadow-md hover:shadow-2xl hover:border-blue-500/50 transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/25">
                  GEMELO DIGITAL WMS v1.0
                </span>
                <div className="p-2.5 rounded-2xl bg-blue-600 text-white shadow-md shadow-blue-500/30 group-hover:scale-110 transition-transform">
                  <CubeIcon className="h-5 w-5" />
                </div>
              </div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                Almacén de Prácticas 3D
              </h3>
              <p className="mt-2 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
                Simulador interactivo de racks, estiba, recepción de mercancías (inbound), preparación de pedidos (outbound) y auditoría física de stock.
              </p>
            </div>
            <div className="mt-6 flex items-center justify-between pt-4 border-t border-slate-100 dark:border-white/[0.06]">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                Abrir Simulador WMS <ArrowRightIcon className="h-3.5 w-3.5" />
              </span>
              <span className="text-[10px] font-semibold text-slate-400">En tiempo real</span>
            </div>
          </Link>

          {/* Hub 3: Casos Prácticos & Despacho Aduanero */}
          <Link
            to="/institucion/simulaciones"
            className="group relative overflow-hidden rounded-[26px] p-6 border border-indigo-500/20 bg-gradient-to-br from-indigo-500/[0.06] via-white/80 to-transparent dark:from-indigo-500/10 dark:via-[#0c0c0c]/80 dark:to-transparent backdrop-blur-xl shadow-md hover:shadow-2xl hover:border-indigo-500/50 transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/25">
                  CASOS Y PEDAGOGÍA
                </span>
                <div className="p-2.5 rounded-2xl bg-indigo-600 text-white shadow-md shadow-indigo-500/30 group-hover:scale-110 transition-transform">
                  <PlayCircleIcon className="h-5 w-5" />
                </div>
              </div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                Casos y Simulaciones
              </h3>
              <p className="mt-2 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
                Resolución de pedimentos aduaneros, análisis de discrepancias documentales en VUCEM, liquidación tributaria y cubicaje logístico.
              </p>
            </div>
            <div className="mt-6 flex items-center justify-between pt-4 border-t border-slate-100 dark:border-white/[0.06]">
              <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                Explorar Catálogo <ArrowRightIcon className="h-3.5 w-3.5" />
              </span>
              <span className="text-[10px] font-semibold text-slate-400">{totalAsignaciones} asignados</span>
            </div>
          </Link>

          {/* Hub 4: Inteligencia Arancelaria LIGIE */}
          <Link
            to="/institucion/tarifas/clasificacion"
            className="group relative overflow-hidden rounded-[26px] p-6 border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.06] via-white/80 to-transparent dark:from-emerald-500/10 dark:via-[#0c0c0c]/80 dark:to-transparent backdrop-blur-xl shadow-md hover:shadow-2xl hover:border-emerald-500/50 transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25">
                  TIGIE VIGENTE & RRNAS
                </span>
                <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/30 group-hover:scale-110 transition-transform">
                  <BookOpenIcon className="h-5 w-5" />
                </div>
              </div>
              <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                Clasificador Arancelario
              </h3>
              <p className="mt-2 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
                Buscador y clasificador arancelario oficial de 8 a 10 dígitos. Notas explicativas de la OMA, regulaciones y restricciones no arancelarias.
              </p>
            </div>
            <div className="mt-6 flex items-center justify-between pt-4 border-t border-slate-100 dark:border-white/[0.06]">
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                Consultar Aranceles <ArrowRightIcon className="h-3.5 w-3.5" />
              </span>
              <span className="text-[10px] font-semibold text-slate-400">Capítulo 01-98</span>
            </div>
          </Link>
        </div>
      </section>

      {/* ======================================================== */}
      {/* 4. WORKSPACE EN 2 COLUMNAS (ASIGNACIONES + SIDEBAR)     */}
      {/* ======================================================== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Columna Izquierda: Mis Casos Asignados + Normatividad (8 Cols) */}
        <div className="lg:col-span-8 space-y-8">
          {/* Panel Bento: Mis Asignaciones */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-sm p-6 sm:p-7">
            {/* Header del Tablero con Filtros */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100 dark:border-white/[0.06]">
              <div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-emerald-500" />
                  Mis Casos y Prácticas Asignadas
                </h3>
                <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium">
                  {asignaciones.length} asignaciones registradas por tus profesores
                </p>
              </div>

              {/* Selector de Filtros por Estado */}
              <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-white/5 rounded-xl border border-slate-200/80 dark:border-white/10 overflow-x-auto">
                {(['todos', 'en_curso', 'pendiente', 'completado'] as const).map((estado) => {
                  const esActivo = filtroEstado === estado
                  const etiquetas = {
                    todos: 'Todos',
                    en_curso: 'En Curso',
                    pendiente: 'Pendientes',
                    completado: 'Completados'
                  }
                  return (
                    <button
                      key={estado}
                      onClick={() => setFiltroEstado(estado)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                        esActivo
                          ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                          : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                      }`}
                    >
                      {etiquetas[estado]}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Buscador de Asignaciones */}
            <div className="mt-5 relative">
              <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Filtrar por título, tipo de caso o grupo..."
                className="w-full pl-10 pr-9 py-2.5 rounded-xl text-xs font-medium bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Listado de Tarjetas de Asignación */}
            <div className="mt-5 space-y-3.5">
              {cargando ? (
                <div className="py-12 flex justify-center items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
                </div>
              ) : asignacionesFiltradas.length > 0 ? (
                asignacionesFiltradas.map((asig) => {
                  const estilo = ESTILOS_ESTADO[asig.estado] || ESTILOS_ESTADO.pendiente
                  const esCompletada = asig.estado === 'completado' || asig.estado === 'calificado'

                  return (
                    <div
                      key={asig.id_asignacion || asig.id_simulacion}
                      className="group p-5 rounded-2xl border border-slate-200/80 dark:border-white/[0.06] bg-slate-50/60 dark:bg-white/[0.02] hover:bg-white dark:hover:bg-white/[0.04] hover:shadow-md transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    >
                      <div className="space-y-2 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                            {asig.tipo}
                          </span>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-[10px] font-bold ${estilo.badge}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${estilo.dot}`} />
                            {estilo.label}
                          </span>
                          {asig.nombre_grupo && (
                            <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold text-slate-500 dark:text-zinc-400 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10">
                              {asig.nombre_grupo}
                            </span>
                          )}
                        </div>

                        <h4 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {asig.titulo}
                        </h4>

                        {asig.descripcion && (
                          <p className="text-xs text-slate-500 dark:text-zinc-400 line-clamp-1">
                            {asig.descripcion}
                          </p>
                        )}

                        {/* Barra de progreso y metadata */}
                        <div className="flex items-center gap-4 pt-1">
                          <div className="flex items-center gap-2 flex-1 max-w-xs">
                            <div className="h-1.5 flex-1 rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  esCompletada
                                    ? 'bg-emerald-500'
                                    : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                                }`}
                                style={{ width: `${asig.progreso}%` }}
                              />
                            </div>
                            <span className="text-[11px] font-mono font-bold text-slate-600 dark:text-zinc-400">
                              {asig.progreso}%
                            </span>
                          </div>

                          {asig.fecha_limite && (
                            <span className="text-[11px] font-medium text-slate-400 flex items-center gap-1">
                              <CalendarIcon className="h-3.5 w-3.5" />
                              Límite: {new Date(asig.fecha_limite).toLocaleDateString()}
                            </span>
                          )}

                          {asig.puntaje !== null && asig.puntaje !== undefined && (
                            <span className="text-[11px] font-bold font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                              Calificación: {asig.puntaje} pts
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Botón de acción */}
                      <div className="shrink-0 flex items-center">
                        <Link
                          to={`/institucion/simulacion/caso-a/${asig.id_simulacion}`}
                          className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm ${
                            esCompletada
                              ? 'bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300'
                              : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-500/20 hover:scale-[1.02]'
                          }`}
                        >
                          <span>{esCompletada ? 'Revisar Caso' : 'Continuar'}</span>
                          <ArrowRightIcon className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="text-center py-12 px-4 rounded-2xl border border-dashed border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-white/[0.01]">
                  <CubeIcon className="h-10 w-10 mx-auto text-slate-300 dark:text-zinc-600 mb-2" />
                  <p className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                    No se encontraron prácticas con este criterio
                  </p>
                  <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                    {busqueda
                      ? 'Prueba modificando el texto de búsqueda o cambiando el filtro de estado.'
                      : 'Actualmente no tienes simulaciones asignadas. Puedes explorar el catálogo general.'}
                  </p>
                  <Link
                    to="/institucion/simulaciones"
                    className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-sm transition-all"
                  >
                    <span>Ver Catálogo de Simulaciones</span>
                    <ArrowRightIcon className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Sección Normatividad Aduanera WMS */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-sm p-6 sm:p-7">
            <SeccionNormatividad basePath="/institucion/normas" />
          </div>
        </div>

        {/* Columna Derecha: Ficha Operador, Cuadrillas y Tips WMS (4 Cols) */}
        <aside className="lg:col-span-4 space-y-6">
          {/* Card 1: Ficha de Identidad del Operador / Alumno */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-sm p-6 relative overflow-hidden">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-white/[0.06]">
              <div className="flex items-center gap-2">
                <IdentificationIcon className="h-5 w-5 text-indigo-500" />
                <h3 className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-zinc-400">
                  Credencial Operativa
                </h3>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                <ShieldCheckIcon className="h-3 w-3" />
                Activo
              </span>
            </div>

            <div className="mt-5 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-cyan-500 text-white font-black text-xl flex items-center justify-center shadow-lg shadow-blue-500/20 shrink-0">
                {(usuario?.nombre || 'O').charAt(0).toUpperCase()}
              </div>
              <div className="overflow-hidden">
                <p className="text-base font-black text-slate-900 dark:text-white truncate">
                  {usuario?.nombre || 'Alumno / Operador'}
                </p>
                <p className="text-xs text-slate-400 font-mono truncate">
                  {usuario?.email || 'operador@logixport.com'}
                </p>
                <span className="mt-1 inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-zinc-400 border border-slate-200 dark:border-white/10">
                  {constitucion?.rol_miembro || 'Alumno / Practicante'}
                </span>
              </div>
            </div>

            <div className="mt-5 p-3 rounded-2xl bg-slate-50/80 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.04] space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Institución:</span>
                <span className="font-bold text-slate-800 dark:text-zinc-200 truncate max-w-[170px]">
                  {constitucion?.nombre || 'No asignada'}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Cuadrilla Principal:</span>
                <span className="font-bold text-slate-800 dark:text-zinc-200">
                  {grupos.length > 0 ? grupos[0].nombre : 'General'}
                </span>
              </div>
            </div>

            <div className="mt-5">
              <Link
                to="/institucion/usuarios/perfil"
                className="w-full py-2.5 px-4 rounded-xl text-xs font-bold text-center block bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 transition-all border border-slate-200/80 dark:border-white/10"
              >
                Ver Mi Perfil Completo
              </Link>
            </div>
          </div>

          {/* Card 2: Mis Grupos y Cuadrillas */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-sm p-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-white/[0.06]">
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-indigo-500" />
                <h3 className="text-sm font-black text-slate-900 dark:text-white">
                  Mis Cuadrillas de Trabajo
                </h3>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-500/20">
                {grupos.length} Activas
              </span>
            </div>

            <div className="mt-4 space-y-2.5">
              {grupos.length > 0 ? (
                grupos.map((g) => (
                  <div
                    key={g.id_grupo}
                    className="p-3.5 rounded-2xl bg-slate-50/80 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.04] flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-extrabold text-xs flex items-center justify-center border border-indigo-500/20">
                        {g.nombre.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-900 dark:text-white">
                          {g.nombre}
                        </p>
                        <p className="text-[11px] text-slate-400 line-clamp-1">
                          {g.descripcion || 'Grupo académico de práctica'}
                        </p>
                      </div>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md">
                      Matriculado
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-slate-400">
                  <UserGroupIcon className="h-8 w-8 mx-auto text-slate-300 dark:text-zinc-600 mb-1" />
                  <p className="text-xs font-semibold">No estás matriculado en ninguna cuadrilla.</p>
                </div>
              )}
            </div>
          </div>

          {/* Card 3: Tips Operativos LogisTech AI (Buenas Prácticas WMS) */}
          <div className="bg-gradient-to-br from-blue-50/70 via-indigo-50/40 to-white dark:from-blue-950/20 dark:via-[#0c0c0c]/80 dark:to-[#0c0c0c] rounded-[28px] border border-blue-200/60 dark:border-blue-500/20 p-6 shadow-sm">
            <div className="flex items-center gap-2 pb-3 border-b border-blue-200/40 dark:border-white/[0.06]">
              <div className="p-1.5 rounded-lg bg-blue-500 text-white">
                <LightBulbIcon className="h-4 w-4" />
              </div>
              <h3 className="text-xs font-black uppercase tracking-widest text-blue-900 dark:text-blue-300">
                LogisTech AI · Consejos WMS
              </h3>
            </div>

            <div className="mt-4 space-y-3.5">
              <div className="text-xs space-y-1">
                <p className="font-bold text-slate-800 dark:text-zinc-200 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  Rotación FIFO / PEPS:
                </p>
                <p className="text-slate-600 dark:text-zinc-400 text-[11px] leading-relaxed pl-3">
                  Asegura que las primeras mercancías en entrar al rack sean las primeras en salir para prevenir obsolescencia y mermas en stock.
                </p>
              </div>

              <div className="text-xs space-y-1">
                <p className="font-bold text-slate-800 dark:text-zinc-200 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                  Cubicaje y Niveles de Carga:
                </p>
                <p className="text-slate-600 dark:text-zinc-400 text-[11px] leading-relaxed pl-3">
                  Coloca pallets de mayor tonelaje en los niveles 1 y 2 de las bahías para mantener el centro de gravedad estructural del almacén.
                </p>
              </div>

              <div className="text-xs space-y-1">
                <p className="font-bold text-slate-800 dark:text-zinc-200 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                  Validación Previa de Pedimento:
                </p>
                <p className="text-slate-600 dark:text-zinc-400 text-[11px] leading-relaxed pl-3">
                  Verifica que los bultos, marcas de origen y factura comercial coincidan exactamente con la fracción TIGIE antes de despachar.
                </p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default PaginaPanelUsuarios
